import React from "react";
import { ArrowUpRight } from "lucide-react";
import { useReveal } from "../lib/useReveal";

const RESOURCES = [
  {
    group: "Museums & archives",
    items: [
      { name: "National Museum of African American History and Culture", url: "https://nmaahc.si.edu/" },
      { name: "National Museum of the American Indian", url: "https://americanindian.si.edu/" },
      { name: "United States Holocaust Memorial Museum", url: "https://www.ushmm.org/" },
      { name: "Library of Congress — African American Odyssey", url: "https://www.loc.gov/exhibits/african-american-odyssey/" },
      { name: "National Archives — Milestone Documents", url: "https://www.archives.gov/milestone-documents" },
      { name: "GLBT Historical Society", url: "https://www.glbthistory.org/" },
      { name: "Stonewall National Monument", url: "https://www.nps.gov/places/stonewall-national-monument.htm" },
      { name: "Greenwood Cultural Center (Tulsa)", url: "https://www.greenwoodculturalcenter.com/" },
    ],
  },
  {
    group: "Toolkits for educators & organizers",
    items: [
      { name: "Teaching Hard History — Learning for Justice", url: "https://www.learningforjustice.org/frameworks/teaching-hard-history" },
      { name: "Zinn Education Project", url: "https://www.zinnedproject.org/" },
      { name: "GLSEN Educator Resources", url: "https://www.glsen.org/resources" },
      { name: "DREDF — Disability Rights Education & Defense Fund", url: "https://www.dredf.org/" },
      { name: "ASALH — Black History themes", url: "https://asalh.org/black-history-themes/" },
      { name: "American Indian History Through Primary Sources", url: "https://americanindian.si.edu/nk360" },
    ],
  },
  {
    group: "Recommended reading",
    items: [
      { name: "The 1619 Project (book or curriculum)", url: "https://1619education.org/" },
      { name: "An Indigenous Peoples' History of the United States — Roxanne Dunbar-Ortiz", url: "https://www.beacon.org/An-Indigenous-Peoples-History-of-the-United-States-P1164.aspx" },
      { name: "Stonewall — David Carter", url: "https://us.macmillan.com/books/9780312671938/stonewall" },
      { name: "Disability Visibility — Alice Wong (ed.)", url: "https://disabilityvisibilityproject.com/book/dv/" },
      { name: "The New Jim Crow — Michelle Alexander", url: "https://newjimcrow.com/" },
      { name: "Bayard Rustin: A Legacy of Protest and Politics", url: "https://www.nyupress.org/9781479830665/bayard-rustin/" },
    ],
  },
];

export const Resources = () => {
  const ref = useReveal();
  return (
    <section id="resources" className="section reveal" ref={ref} data-testid="resources-section" style={{ background: "hsl(var(--bg-deep))" }}>
      <div className="shell">
        <span className="section__eyebrow">Resources</span>
        <h2 style={{ marginTop: 0 }}>Where to keep learning.</h2>
        <p className="section__lede" style={{ marginTop: "1rem", maxWidth: 720 }}>
          A short, opinionated reading and visiting list. We chose institutions and projects that center
          primary sources, community voices, and the work of people most affected by these histories.
        </p>

        <div style={{
          marginTop: "3rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "2rem",
        }}>
          {RESOURCES.map((group) => (
            <div key={group.group}>
              <h3 style={{
                fontSize: "0.85rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "hsl(var(--accent))",
                fontFamily: "var(--font-body)",
                fontWeight: 600,
                marginBottom: "1rem",
                paddingBottom: "0.5rem",
                borderBottom: "1px solid hsl(var(--accent) / 0.3)",
              }}>
                {group.group}
              </h3>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 14 }}>
                {group.items.map((item) => (
                  <li key={item.url}>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="resource-link"
                      style={{
                        display: "inline-flex",
                        alignItems: "flex-start",
                        gap: 6,
                        textDecoration: "none",
                        color: "hsl(var(--ink))",
                        fontSize: "0.95rem",
                        lineHeight: 1.45,
                        borderBottom: "1px solid transparent",
                        transition: "color 160ms ease, border-color 160ms ease",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "hsl(var(--accent))"; e.currentTarget.style.borderColor = "hsl(var(--accent))"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "hsl(var(--ink))"; e.currentTarget.style.borderColor = "transparent"; }}
                    >
                      <span>{item.name}</span>
                      <ArrowUpRight size={14} aria-hidden style={{ flexShrink: 0, marginTop: 4, color: "hsl(var(--accent))" }} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
